import {
    EffectDescription,
    FilterDescription,
    TitleDescription,
    TransitionDescription
} from "../../librettoEditor/config/config";
import {makeAutoObservable} from "mobx";
import {ThemeVariants} from "../../librettoEditor/store/ApplicationStore";

type DraggingObjectPayload =
    | {
    type: "media";
    payload: any;
}
    | {
    type: "text-preset";
    payload: TitleDescription;
};

class SequenceApplicationStoreInstance {
    isLoading: boolean = true;
    isRendering: boolean = false;
    selectedClipId: string | null = null;
    selectedClipIds: string[] = [];
    selectedLayerId: string | null = null;
    selectedTransitionId: string | null = null;
    subtitlesClipId: string | null = null;
    transitions: Record<string, TransitionDescription> = {};
    filters: Record<string, FilterDescription> = {};
    effects: Record<string, EffectDescription> = {};
    draggingObject: DraggingObjectPayload | null = null;
    selectedRange: {start: number; end: number} | null = null;
    rangeToolEnabled: boolean = false;
    theme: ThemeVariants = "system";
    filtersPath: string | null = null;
    effectsPath: string | null = null;
    transitionsPath: string | null = null;
    titlesPath: string | null = null;
    subtitleStylesPath: string | null = null;
    animationsPath: string | null = null;

    constructor() {
        makeAutoObservable(this);
    }

    setIsLoading(value: boolean) {
        this.isLoading = value;
    }

    setIsRendering(value: boolean) {
        this.isRendering = value;
    }

    setSelectedLibrettoClipId(id: string) {
        this.setSelectedClipId(id);
    }

    setSelectedClipId(id: string | null) {
        this.selectedClipId = id;
        this.selectedClipIds = id ? [id] : [];
        this.setSelectedTransitionId(null);
    }

    setSelectedClipIds(ids: string[], activeClipId?: string | null) {
        const normalized = Array.from(
            new Set(
                (ids || [])
                    .map((id) => String(id ?? "").trim())
                    .filter(Boolean)
            )
        );
        this.selectedClipIds = normalized;

        if (activeClipId !== undefined) {
            const nextActive = activeClipId ? String(activeClipId) : null;
            this.selectedClipId = nextActive && normalized.includes(nextActive)
                ? nextActive
                : (normalized[0] ?? null);
        } else if (!this.selectedClipId || !normalized.includes(this.selectedClipId)) {
            this.selectedClipId = normalized[0] ?? null;
        }

        this.setSelectedTransitionId(null);
    }

    toggleClipSelection(id: string) {
        const clipId = String(id ?? "").trim();
        if (!clipId) return;
        if (this.selectedClipIds.includes(clipId)) {
            this.removeClipFromSelection(clipId);
            return;
        }
        this.setSelectedClipIds([...this.selectedClipIds, clipId], clipId);
    }

    removeClipFromSelection(id: string) {
        const clipId = String(id ?? "").trim();
        if (!clipId) return;
        if (!this.selectedClipIds.includes(clipId)) return;
        const next = this.selectedClipIds.filter((existingId) => existingId !== clipId);
        const nextActive = this.selectedClipId === clipId ? (next[next.length - 1] ?? null) : this.selectedClipId;
        this.setSelectedClipIds(next, nextActive);
    }

    clearClipSelection() {
        this.setSelectedClipId(null);
    }

    setSelectedLayerId(id: string | null) {
        this.selectedLayerId = id;
    }

    setSelectedLibrettoClipIdToNull() {
        SequenceApplicationStore.setRangeToolEnabled(false);
        this.clearClipSelection();
    }

    setSubtitlesLibrettoClipId(id: string | null) {
        this.subtitlesClipId = id;
    }

    setSelectedTransitionId(id: string | null) {
        this.selectedTransitionId = id;
    }

    setDraggingObject(draggingObject: DraggingObjectPayload | null) {
        this.draggingObject = draggingObject;
    }

    setRangeToolEnabled(value: boolean) {
        this.rangeToolEnabled = value;
    }

    setSelectedRange(value: {start: number, end: number}) {
        this.selectedRange = value;
    }

    setTheme(value: ThemeVariants = "dark") {
        if (value === "system") {
            const isDarkTheme = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
            SequenceApplicationStore.theme = isDarkTheme ? "dark" : "light";
        } else {
            SequenceApplicationStore.theme = value;
        }
    }

    setFiltersPath(path: string | null) {
        this.filtersPath = path;
    }

    setEffectsPath(path: string | null) {
        this.effectsPath = path;
    }

    setTransitionsPath(path: string | null) {
        this.transitionsPath = path;
    }

    setSubtitleStylesPath(path: string | null) {
        this.subtitleStylesPath = path;
    }

    setAnimationsPath(path: string | null) {
        this.animationsPath = path;
    }

    setTitlesPath(path: string | null) {
        this.titlesPath = path;
    }

    setTransitions(transitions: Record<string, TransitionDescription>) {
        this.transitions = transitions;
    }

    setFilters(filters: Record<string, FilterDescription>) {
        this.filters = filters;
    }

    setEffects(effects: Record<string, EffectDescription>) {
        this.effects = effects;
    }

    reset() {
        this.isLoading = true;
        this.isRendering = false;
        this.selectedClipId = null;
        this.selectedClipIds = [];
        this.selectedLayerId = null;
        this.selectedTransitionId = null;
        this.subtitlesClipId = null;
        this.transitions = {};
        this.filters = {};
        this.effects = {};
        this.draggingObject = null;
        this.rangeToolEnabled = false;
        this.theme = "system";
        this.filtersPath = null;
        this.effectsPath = null;
        this.transitionsPath = null;
        this.titlesPath = null;
        this.subtitleStylesPath = null;
        this.animationsPath = null;
    }
}

const SequenceApplicationStore = new SequenceApplicationStoreInstance();

export { SequenceApplicationStore };
